import * as React from 'react';
import cx from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';

import {
  Clickable,
  type Props as ClickableProps,
} from '../../../../components/Clickable/Clickable';
import { WrapList } from '../../../../components/WrapList/WrapList';
import { Legal } from '../../../../components/Legal/Legal';
import { useSiteMetadata } from '../../../../utils/useSiteMetadata';
import { Link } from '../../../../components/Link/Link';

import martian from './assets/martian.image.svg';
import martianMain from './assets/martian-main.image.svg';

import * as cs from './FooterWide.module.css';

interface Props {
  page: string;
  showEmailNewsletterForm: boolean;
}

interface SocialLink extends ClickableProps {
  key: string;
  label?: string;
}

const SOCIAL_LINKS = [
  { key: 'github' },
  { key: 'linkedin' },
  { key: 'twitter', label: 'X' },
  { key: 'mastodon' },
  { key: 'instagram' },
  { key: 'youtube' },
  { key: 'dribbble' },
  { key: 'facebook' },
];

const COMPANY_LINKS = [
  { key: 'wellfound', label: 'Careers' },
  { key: 'japanese', label: '日本語版', lang: 'ja' },
];

export const FooterWide = ({ page }: Props) => {
  const siteMetadata = useSiteMetadata();

  const mainPage = page === 'main';

  const { snippet } = useStaticQuery(graphql`
    query {
      snippet(slug: { eq: "footer" }) {
        id
        data
      }
    }
  `);

  const { credentials } = snippet.data;

  return (
    <footer
      className={cx(cs.wrapper, { [cs.wrapperMain]: mainPage })}
      data-theme-reset={!mainPage}
    >
      <div className={cx(cs.footer, { [cs.footerMain]: mainPage })}>
        <div className={cs.end}>
          <div>
            <Link className={cs.aloneLink} to={`mailto:${siteMetadata.email}`}>
              {siteMetadata.email}
            </Link>
            <WrapList<SocialLink>
              className={cs.socials}
              entries={SOCIAL_LINKS}
              getKey={(entry) => entry.key}
              renderItem={(entry) => (
                <Clickable
                  className={cs.linkWrapper}
                  activeClassName={cs.active}
                  key={entry.key}
                  to={siteMetadata[entry.key]}
                  target="_blank"
                  trackEvent="footer-socials-click"
                  trackLabel={entry.key}
                  rel="noopener noreferrer me"
                  lang={entry.lang}
                >
                  <span className={cs.link}>{entry.label || entry.key}</span>
                </Clickable>
              )}
              columnGap={4}
              rowGap={0}
            />
          </div>

          <img
            src={mainPage ? martianMain : martian}
            alt=""
            className={cs.martian}
            aria-hidden
          />

          <div>
            <ul className={cs.companyLinks}>
              <li>
                <Clickable
                  className={cs.linkWrapper}
                  activeClassName={cs.activeLink}
                  to="/contact-us"
                  trackEvent="footer-socials-click"
                  trackLabel="contact-us-link"
                >
                  <span className={cs.link}>Contact us</span>
                </Clickable>
              </li>
              {COMPANY_LINKS.map((entry) => (
                <li key={entry.key}>
                  <Clickable
                    className={cs.linkWrapper}
                    activeClassName={cs.active}
                    to={siteMetadata[entry.key]}
                    target="_blank"
                    trackEvent="footer-socials-click"
                    trackLabel={entry.key}
                    rel="noopener noreferrer me"
                    lang={entry.lang}
                  >
                    <span className={cs.link}>{entry.label || entry.key}</span>
                  </Clickable>
                </li>
              ))}
            </ul>
            <Legal />
            {credentials.map((credential: any) => (
              <p key={credential} className={cs.credential}>
                {credential}
              </p>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
